import * as React from 'react';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Dialog, DialogUtils } from '@shared/ui-react/Dialog';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
import { Button } from '@shared/ui-react/Button';
import { Ajax2 } from '@shared/Ajax';
import { closestCenter, DndContext, DragEndEvent, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { Notify } from '@shared/ui-native/Notify';

export const LogpointsDownloadDialog = {
    open: (logpointIds?: number[], onDownload?: (columns: string[], format: LogpointExportFileType) => void) => {
        if (!kuura.hasPrivilege('Download_LogPoint')) {
            Notify.warn(__('general.validation.access_not_allowed'));
            return;
        }

        const targetEl = document.getElementById('react-portal-root');
        ReactUtils.safeRender(
            <>
                <Dialog onClose={DialogUtils.close} title={__('js_logpoints.export_logpoints')}>
                    <LogpointsDownloadDialogComponent logpointIds={logpointIds} onDownload={onDownload} />
                </Dialog>
            </>,
            targetEl
        );
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

@observer
export class LogpointsDownloadDialogComponent extends React.Component<{
    logpointIds?: number[];
    onDownload?: (columns: string[], format: LogpointExportFileType) => void;
}> {
    @observable.ref presets = null;
    @observable isLoading = false;
    @observable columns = Object.entries(LogpointsService.importableProperties).map(([value, title]) => ({
        value: value.toLowerCase(),
        title,
        active: true,
    }));

    constructor(props: any) {
        super(props);
        makeObservable(this);
        this.fetchDialogData();
    }

    render() {
        if (!this.presets) {
            return <div className='overlay-loading' style={{ width: 300, height: 150 }}></div>;
        }
        return (
            <>
                <div className='padded flex-column of-h'>
                    <Dropdown closeOnBodyClick={true}>
                        <span>
                            {__('js_logpoints.import.load_columns_preset')} <i className='iconf-caret' />
                        </span>
                        <div>
                            {this.presets.length === 0 ? (
                                <div className='padded'>{__('js_logpoints.presets.none')}</div>
                            ) : (
                                <ul className='dropdown-list clickable'>
                                    {this.presets.map((preset, i) => (
                                        <li key={i} title={preset.t} onClick={() => this.loadPreset(preset.t)}>
                                            {preset.n || preset.t}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </Dropdown>
                    <hr />
                    <h6>{__('js_files.import_csv.columns')}</h6>
                    <div style={{ maxHeight: 500 }} className='of-a'>
                        <ItemsList items={this.columns} onSortEnd={this.onSortEnd} />
                    </div>

                    <hr />

                    <div className='flex-row gap-half' style={{ width: 330, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button icon='download' className='ik-button-primary' onClick={() => this.download('csv')}>
                            CSV
                        </Button>
                        <Button icon='download' className='ik-button-primary' onClick={() => this.download('gt')}>
                            GT
                        </Button>
                        <Button icon='download' className='ik-button-primary' onClick={() => this.download('inframodel')}>
                            InframodelXML
                        </Button>
                        <Button icon='download' className='ik-button-primary' onClick={() => this.download('dxf')}>
                            DXF
                        </Button>
                        <Button icon='download' className='ik-button-primary' onClick={() => this.download('kof')}>
                            kof
                        </Button>
                        <Button icon='download' className='ik-button-primary' onClick={() => this.download('xml')}>
                            LandXML
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    download = (fileType: LogpointExportFileType) => {
        const columnIds = Object.values(this.columns)
            .filter(c => c.active)
            .map(c => c.value);

        if (this.props.logpointIds) {
            LogpointsService.download(fileType, this.props.logpointIds, columnIds);
        } else {
            this.props.onDownload(columnIds, fileType);
        }

        setTimeout(() => {
            DialogUtils.close();
        }, 300);
    };

    fetchDialogData = async () => {
        this.isLoading = true;

        try {
            this.presets = await Ajax2({
                url: `${kuura.getContextPath()}/api/1/project/${kuura.getProjectUuid()}/property/${LogpointsService.importExportPresetsStorageKey}`,
            }).then(resp => {
                if (resp.projectProperty?.value) {
                    try {
                        return JSON.parse(resp.projectProperty?.value);
                    } catch {
                        return [];
                    }
                }
            });
        } catch {
            this.presets = [];
        } finally {
            this.isLoading = false;
        }
    };

    loadPreset = preset => {
        const presetColumns = LogpointsService.getColumnsFromPreset(preset, true);
        runInAction(() => {
            this.columns.forEach(c => (c.active = presetColumns.includes(c.value)));

            this.columns.sort((a, b) => {
                if (!a.active && !b.active) return -1;
                if (!a.active && b.active) return 1;
                if (a.active && !b.active) return -1;
                return presetColumns.indexOf(a.value) - presetColumns.indexOf(b.value);
            });
        });
    };

    onSortEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = this.columns.findIndex(item => item.value === active.id);
            const newIndex = this.columns.findIndex(item => item.value === over.id);

            const columns = [...this.columns];
            this.columns = arrayMove(columns, oldIndex, newIndex);
        }
    };
}

const ItemsList = observer(({ items, onSortEnd }) => {
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSortEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
            <SortableContext items={items.map(item => item.value)} strategy={verticalListSortingStrategy}>
                <div>
                    {items.map(item => (
                        <SortableListItem key={`item-${item.value}`} id={item.value} item={item} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
});

const ListItem = observer(({ item, dragHandleProps = null, style = {} }) => (
    <div className='sortable-list__item' style={{ padding: 0, ...style }}>
        <div className='sortable__drag-handle' {...dragHandleProps}>
            ፧
        </div>
        <label className='checkbox-label' style={{ width: '100%' }}>
            <input type='checkbox' checked={item.active} onClick={() => (item.active = !item.active)} readOnly />
            <span>{item.title}</span>
        </label>
    </div>
));

const SortableListItem = observer(({ id, item }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const dragHandleProps = {
        ...attributes,
        ...listeners,
    };

    return (
        <div ref={setNodeRef}>
            <ListItem item={item} dragHandleProps={dragHandleProps} style={style} />
        </div>
    );
});
